/**
 * ScenarioTable — DataTable + TableToolbar wrapper for Step 3.
 *
 * Read mode + CRUD landed together in Story 5.A (CSV import/export and
 * hierarchical two-tab arrive in 5.B / 5.C). Mirrors the Step 2
 * AssetTable shape; the only structural difference is the per-row
 * compromised-properties / final-impact-rating columns.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
  RowActionsMenu,
} from '../../../../components/ui/DataTable';
import FilterDrawer from '../../../../components/ui/DataTable/FilterDrawer';
import { useImpactCriteria } from '../../hooks/useImpactCriteria';

/** Impact columns -> the configured category whose criteria explain them.
 *  `final_impact_rating` is the MAX of the four, so it has no category of its
 *  own and is deliberately absent. */
const IMPACT_CATEGORY_BY_COLUMN: Record<string, string> = {
  safety: 'Safety',
  financial: 'Financial',
  operational: 'Operational',
  privacy: 'Privacy',
};
import {
  SCENARIO_COLUMNS_READ,
  SCENARIO_COLUMNS_EDIT,
  SCENARIO_ACTIONS_COLUMN_WIDTH,
  type ScenarioRecord,
} from './scenarioFieldSchema';
import styles from './ScenarioTable.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;
const RowActionsMenuAny = RowActionsMenu as unknown as React.ComponentType<any>;
const FilterDrawerAny = FilterDrawer as unknown as React.ComponentType<any>;
// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors the
// ButtonAny convention across the step-components).
const ButtonAny = Button as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

import { Icon } from '../../../../components/ui/Icon';
import { Button } from '../../../../components/ui/Button2';

interface SortState {
  column: string;
  direction: 'asc' | 'desc';
}

interface FilterOption {
  value: string;
  label: string;
  count: number;
}

// SFOP / overall-impact levels order by severity (low → high), not
// alphabetically, so the filter sections read like the rating scale.
const SEVERITY_ORDER = ['negligible', 'minor', 'moderate', 'major', 'severe'];
const severityRank = (v: string): number => {
  const i = SEVERITY_ORDER.indexOf(v.toLowerCase());
  return i === -1 ? SEVERITY_ORDER.length : i;
};

/** Distinct single-value options + counts for a scenario field.
 *  `bySeverity` orders by the rating scale; otherwise alphabetical. */
function singleValueOptions(
  rows: ScenarioRecord[],
  key: string,
  bySeverity: boolean,
): FilterOption[] {
  const counts: Record<string, number> = {};
  for (const r of rows) {
    const k = String((r as Record<string, unknown>)[key] ?? '').trim();
    if (k) counts[k] = (counts[k] ?? 0) + 1;
  }
  const keys = Object.keys(counts);
  keys.sort(bySeverity
    ? (a, b) => severityRank(a) - severityRank(b) || a.localeCompare(b)
    : (a, b) => a.localeCompare(b));
  return keys.map((v) => ({ value: v, label: v, count: counts[v] }));
}

/** Distinct options + counts across an array-valued field (e.g.
 *  compromised_properties), counting one per row that contains it. */
function arrayValueOptions(rows: ScenarioRecord[], key: string): FilterOption[] {
  const counts: Record<string, number> = {};
  for (const r of rows) {
    const arr = (r as Record<string, unknown>)[key];
    if (Array.isArray(arr)) {
      for (const p of arr) {
        const k = String(p).trim();
        if (k) counts[k] = (counts[k] ?? 0) + 1;
      }
    }
  }
  return Object.keys(counts).sort().map((v) => ({ value: v, label: v, count: counts[v] }));
}

export interface ScenarioTableProps {
  scenarios: ScenarioRecord[];
  loading?: boolean;
  emptyMessage?: string;
  editable?: boolean;
  onAdd?: () => void;
  onEdit?: (scenario: ScenarioRecord) => void;
  onDelete?: (scenario: ScenarioRecord) => void;
  /** Export the current filtered/sorted scenario list to CSV. */
  onExport?: () => void;
  /** Receive a parsed import file (CSV or JSON) for bulk-add. */
  onImport?: (file: File) => void;
}

function sortScenarios(rows: ScenarioRecord[], sort: SortState | null): ScenarioRecord[] {
  if (!sort) return rows;
  const dir = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = (a as Record<string, unknown>)[sort.column];
    const bv = (b as Record<string, unknown>)[sort.column];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  });
}

function searchScenarios(rows: ScenarioRecord[], query: string): ScenarioRecord[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) =>
    Object.values(r).some((v) =>
      v == null
        ? false
        : Array.isArray(v)
        ? v.join(' ').toLowerCase().includes(q)
        : String(v).toLowerCase().includes(q),
    ),
  );
}

export default function ScenarioTable({
  scenarios,
  loading,
  emptyMessage = 'No damage scenarios yet.',
  editable = false,
  onAdd,
  onEdit,
  onDelete,
  onExport,
  onImport,
}: ScenarioTableProps) {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [compromisedValues, setCompromisedValues] = useState<string[]>([]);
  const [safetyValues, setSafetyValues] = useState<string[]>([]);
  const [financialValues, setFinancialValues] = useState<string[]>([]);
  const [operationalValues, setOperationalValues] = useState<string[]>([]);
  const [privacyValues, setPrivacyValues] = useState<string[]>([]);
  const [impactValues, setImpactValues] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  // Options + counts derived from the live scenario list so each section
  // reflects the agent's actual values. SFOP + overall impact order by
  // severity; compromised properties alphabetical.
  const compromisedOptions = useMemo(() => arrayValueOptions(scenarios, 'compromised_properties'), [scenarios]);
  const safetyOptions = useMemo(() => singleValueOptions(scenarios, 'safety', true), [scenarios]);
  const financialOptions = useMemo(() => singleValueOptions(scenarios, 'financial', true), [scenarios]);
  const operationalOptions = useMemo(() => singleValueOptions(scenarios, 'operational', true), [scenarios]);
  const privacyOptions = useMemo(() => singleValueOptions(scenarios, 'privacy', true), [scenarios]);
  const impactOptions = useMemo(() => singleValueOptions(scenarios, 'final_impact_rating', true), [scenarios]);

  // All active filters applied in one pass: compromised is array-membership
  // (keep a row carrying ANY selected property); SFOP + impact are
  // single-value matches. Search runs last.
  const filtered = useMemo(() => {
    const matchSingle = (val: unknown, sel: string[]) =>
      sel.length === 0 || sel.includes(String(val ?? ''));
    const passed = scenarios.filter((s) => {
      if (
        compromisedValues.length > 0 &&
        !(Array.isArray(s.compromised_properties) &&
          s.compromised_properties.some((p) => compromisedValues.includes(String(p))))
      ) return false;
      if (!matchSingle(s.safety, safetyValues)) return false;
      if (!matchSingle(s.financial, financialValues)) return false;
      if (!matchSingle(s.operational, operationalValues)) return false;
      if (!matchSingle(s.privacy, privacyValues)) return false;
      if (!matchSingle(s.final_impact_rating, impactValues)) return false;
      return true;
    });
    return searchScenarios(passed, search);
  }, [
    scenarios, compromisedValues, safetyValues, financialValues,
    operationalValues, privacyValues, impactValues, search,
  ]);
  const sorted = useMemo(() => sortScenarios(filtered, sort), [filtered, sort]);

  const filters = [
    { key: 'compromised_properties', label: 'Compromised', values: compromisedValues,
      onChange: (next: string[]) => { setCompromisedValues(next); setPage(1); }, options: compromisedOptions },
    { key: 'safety', label: 'Safety', values: safetyValues,
      onChange: (next: string[]) => { setSafetyValues(next); setPage(1); }, options: safetyOptions },
    { key: 'financial', label: 'Financial', values: financialValues,
      onChange: (next: string[]) => { setFinancialValues(next); setPage(1); }, options: financialOptions },
    { key: 'operational', label: 'Operational', values: operationalValues,
      onChange: (next: string[]) => { setOperationalValues(next); setPage(1); }, options: operationalOptions },
    { key: 'privacy', label: 'Privacy', values: privacyValues,
      onChange: (next: string[]) => { setPrivacyValues(next); setPage(1); }, options: privacyOptions },
    { key: 'final_impact_rating', label: 'Overall Impact', values: impactValues,
      onChange: (next: string[]) => { setImpactValues(next); setPage(1); }, options: impactOptions },
  ];
  const anyFilterActive =
    compromisedValues.length + safetyValues.length + financialValues.length +
    operationalValues.length + privacyValues.length + impactValues.length > 0;

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paged = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize],
  );

  // The agent merges scenarios from multiple child TARAs without tagging
  // each row with a source — so `damage_id` is NOT unique in hierarchical
  // sessions (DS-001 may appear three times). React reconciliation needs
  // a unique key per rendered row or sorting visibly swaps content under
  // duplicate keys. Inject a synthetic position-stable key here; the
  // original `damage_id` is preserved on the row for CRUD lookups.
  // Story 5.B's hierarchical split eliminates this within each sub-table.
  const keyed = useMemo(
    () =>
      paged.map((r, i) => ({
        ...r,
        _rowKey: `${r.damage_id ?? 'row'}::${(page - 1) * pageSize + i}`,
      })),
    [paged, page, pageSize],
  );

  // Explain each impact rating from the tenant's configured criteria, so a
  // reviewer can see what "Severe" means here rather than taking it on trust.
  const { describe } = useImpactCriteria();
  const withCriteria = useCallback(
    (cols: any[]) =>
      cols.map((c) => {
        const category = IMPACT_CATEGORY_BY_COLUMN[c.key];
        if (!category || typeof c.render !== 'function') return c;
        return {
          ...c,
          render: (row: ScenarioRecord) => {
            const explanation = describe(category, (row as any)[c.key]);
            const cell = c.render(row);
            return explanation ? (
              <span title={explanation} style={{ cursor: 'help' }}>{cell}</span>
            ) : (
              cell
            );
          },
        };
      }),
    [describe],
  );

  const columns = useMemo(() => {
    if (!editable) return withCriteria(SCENARIO_COLUMNS_READ);
    return [
      ...withCriteria(SCENARIO_COLUMNS_EDIT),
      {
        key: '_actions',
        label: '',
        align: 'right',
        width: SCENARIO_ACTIONS_COLUMN_WIDTH,
        sortable: false,
        render: (row: ScenarioRecord) => (
          <RowActionsMenuAny
            ariaLabel={`Actions for ${row.damage_id}`}
            actions={[
              { id: 'edit', label: 'Edit', icon: <Icon name="edit" size={14} />, onClick: () => onEdit?.(row) },
              { id: 'delete', label: 'Delete', icon: <Icon name="delete" size={14} />, danger: true, onClick: () => onDelete?.(row) },
            ]}
          />
        ),
      },
    ];
  }, [editable, onEdit, onDelete, withCriteria]);

  const toolbarActions = (
    <div className={styles.toolbarActions}>
      {onExport && sorted.length > 0 && (
        <ButtonAny variant="outline" size="sm" onClick={onExport}>
          <Icon name="upload" size={14} /> Export CSV
        </ButtonAny>
      )}
      {editable && onImport && (
        <>
          <ButtonAny
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Icon name="download" size={14} /> Import
          </ButtonAny>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onImport(f);
              // Reset so re-importing the same filename still triggers
              if (e.target) e.target.value = '';
            }}
          />
        </>
      )}
      {editable && onAdd && (
        <ButtonAny variant="default" size="sm" onClick={onAdd}>
          <Icon name="add" size={14} /> Add Scenario
        </ButtonAny>
      )}
    </div>
  );

  return (
    <div className={styles.tableShell}>
      <FilterDrawerAny
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
      />
      <DataTableAny
        columns={columns}
        data={keyed}
        rowKey="_rowKey"
        sort={sort}
        onSortChange={(column: string | null, direction: 'asc' | 'desc' | null) =>
          setSort(column && direction ? { column, direction } : null)
        }
        loading={loading}
        emptyMessage={emptyMessage}
        toolbar={
          <TableToolbarAny
            search={{
              value: search,
              onChange: (v: string) => {
                setSearch(v);
                setPage(1);
              },
              placeholder: 'Search scenarios…',
            }}
            count={{ value: sorted.length, label: 'Scenarios' }}
            onFilterToggle={() => setFilterOpen((o) => !o)}
            filterButtonActive={filterOpen || anyFilterActive}
            actions={toolbarActions}
          />
        }
        pagination={
          <TablePaginationAny
            currentPage={page}
            totalItems={sorted.length}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(size: number) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        }
      />
    </div>
  );
}
